import React, { useState, useRef, useEffect } from 'react';
import { X, Eye, EyeOff, ExternalLink, Megaphone, AlertCircle, Info, AlertTriangle, Briefcase, ArrowRight, ArrowLeft, Images, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useChatPopup } from './ChatPopupManager';
import { BidirectionalChat } from '@/components/messaging/BidirectionalChat';
import { MediaCarousel } from '@/components/messaging/MediaCarousel';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { supabase } from '@/integrations/supabase/client';
import { getUserEntityId } from '@/lib/chat-utils';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { broadcastsApi } from '@/api/broadcasts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';

// Helper to convert URLs in text to clickable links
// Uses colors that work well on broadcast card backgrounds
const linkifyText = (text: string): React.ReactNode => {
  const urlRegex = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s]|www\.[^\s<]+[^<.,:;"')\]\s])/gi;
  const parts = text.split(urlRegex);
  const matches = text.match(urlRegex) || [];
  if (matches.length === 0) return text;
  const result: React.ReactNode[] = [];
  parts.forEach((part, index) => {
    if (part) {
      if (matches.includes(part)) {
        const href = part.startsWith('www.') ? `https://${part}` : part;
        result.push(
          <a
            key={`link-${index}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 underline underline-offset-2 break-all"
            onClick={(e) => e.stopPropagation()}
          >
            {part}
          </a>
        );
      } else {
        result.push(part);
      }
    }
  });
  return result;
};

interface ChatWindowProps {
  chat: {
    id: string;
    name: string;
    avatar?: string;
    role: 'client' | 'filmmaker';
    isMinimized: boolean;
    unreadCount?: number;
  };
  position: number;
  isMobile?: boolean;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({ chat, position, isMobile = false }) => {
  const { closeChat, currentUserRole, toggleChatList, closeChatList } = useChatPopup();
  const [isTransparent, setIsTransparent] = useState(false);
  const [carouselMedia, setCarouselMedia] = useState<{
    isOpen: boolean;
    mediaId: string;
    messageId?: string;
  }>({ isOpen: false, mediaId: '', messageId: undefined });
  const queryClient = useQueryClient();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [currentEntityId, setCurrentEntityId] = useState<string>('');
  const [currentUserName, setCurrentUserName] = useState<string>('');
  const [currentUserTier, setCurrentUserTier] = useState<'free' | 'pro'>('free');
  const navigate = useNavigate();

  // Admin client ID for detecting support chats
  const ADMIN_CLIENT_ID = '7c5377d6-727d-4c6e-bbcb-4b4c34009098';

  // Ensure body scrolling is not blocked when transparent
  useEffect(() => {
    if (isMobile && isTransparent) {
      document.body.style.overflow = 'auto';
      document.body.style.position = 'static';
      
      return () => {
        // Don't change anything on cleanup to avoid affecting other components
      };
    }
  }, [isMobile, isTransparent]);

  // Get current user info - cached with React Query for instant availability
  const { data: cachedUserInfo } = useQuery({
    queryKey: ['user-info-chatwindow', currentUserRole],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const entityId = await getUserEntityId(user.id, currentUserRole || 'filmmaker');
      return {
        userId: user.id,
        entityId,
        userName: user.user_metadata?.full_name || user.email?.split('@')[0] || 'User',
      };
    },
    enabled: true,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Update state when cached user info is available
  useEffect(() => {
    if (cachedUserInfo) {
      if (cachedUserInfo.userId !== currentUserId) {
        setCurrentUserId(cachedUserInfo.userId);
      }
      if (cachedUserInfo.entityId !== currentEntityId) {
        setCurrentEntityId(cachedUserInfo.entityId);
      }
      if (cachedUserInfo.userName !== currentUserName) {
        setCurrentUserName(cachedUserInfo.userName);
      }
    }
  }, [cachedUserInfo]);

  // Fetch current user's subscription tier - check directly from users table
  useEffect(() => {
    const fetchCurrentUserTier = async () => {
      if (!currentUserId) return;

      try {
        // Check subscription tier from users table directly
        const { data: userData } = await supabase
          .from('users')
          .select('subscription_tier')
          .eq('user_id', currentUserId)
          .single();

        setCurrentUserTier(userData?.subscription_tier || 'free');
      } catch (error) {
        console.log('Could not fetch current user subscription tier:', error);
      }
    };

    fetchCurrentUserTier();
  }, [currentUserId]);

  // Check if this is a broadcast chat
  const isBroadcast = chat.id === 'broadcast';

  // Fetch broadcasts if this is a broadcast chat
  const { data: broadcasts, isLoading: broadcastsLoading } = useQuery({
    queryKey: ['user-broadcasts', currentUserRole],
    queryFn: () => broadcastsApi.getMyBroadcasts(currentUserRole || 'filmmaker'),
    enabled: isBroadcast && !!currentUserRole,
    refetchInterval: 120000, // Only needed when broadcast chat is open; 2 min is sufficient
  });

  // Mark broadcasts as viewed when opening the broadcast chat
  useEffect(() => {
    if (isBroadcast && broadcasts && broadcasts.length > 0) {
      const viewedBroadcasts = JSON.parse(localStorage.getItem('viewedBroadcasts') || '[]');
      const activeBroadcastIds = broadcasts
        .filter(b => b.is_active)
        .map(b => b.id);
      
      const newViewedBroadcasts = [...new Set([...viewedBroadcasts, ...activeBroadcastIds])];
      localStorage.setItem('viewedBroadcasts', JSON.stringify(newViewedBroadcasts));
      
      queryClient.invalidateQueries({ queryKey: ['user-broadcasts'] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    }
  }, [isBroadcast, broadcasts, queryClient]);

  // Prepare user and recipient info for BidirectionalChat - exactly like main messages page
  const userInfo = React.useMemo(() => {
    if (!currentEntityId || !currentUserName) return null;
    return {
      id: currentEntityId,
      name: currentUserName,
      role: (currentUserRole || 'filmmaker') as 'filmmaker' | 'client',
    };
  }, [currentEntityId, currentUserName, currentUserRole]);
  
  // Extract recipient ID from room name format: chat_client_[clientId]_filmmaker_[filmmakerId]
  const recipientInfo = React.useMemo(() => {
    if (isBroadcast) return null;

    let recipientId = '';
    let recipientRole: 'client' | 'filmmaker' = chat.role;

    // Check if this is a direct entity ID (like admin chat)
    // Admin client ID format: 7c5377d6-727d-4c6e-bbcb-4b4c34009098
    if (chat.id && chat.id.match(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/)) {
      recipientId = chat.id;
    } else if (chat.id && chat.id.includes('_')) {
      // Standard room name format
      const parts = chat.id.split('_');
      if (parts.length === 5) {
        const clientIdInRoom = parts[2];
        const filmmakerIdInRoom = parts[4];

        // Check if admin is in this room (could be at either position)
        if (clientIdInRoom === ADMIN_CLIENT_ID) {
          // Admin is the client in this room
          recipientId = ADMIN_CLIENT_ID;
          recipientRole = 'client';
        } else if (filmmakerIdInRoom === ADMIN_CLIENT_ID) {
          // Admin is the filmmaker in this room
          recipientId = ADMIN_CLIENT_ID;
          recipientRole = 'filmmaker';
        } else {
          // Normal chat, extract the other person's ID
          if (currentUserRole === 'client') {
            recipientId = filmmakerIdInRoom;
            recipientRole = 'filmmaker';
          } else {
            recipientId = clientIdInRoom;
            recipientRole = 'client';
          }
        }
      }
    }

    if (!recipientId) return null;

    return {
      id: recipientId,
      role: recipientRole,
      name: chat.name,
      avatar: chat.avatar,
    };
  }, [chat, currentUserRole, isBroadcast, ADMIN_CLIENT_ID]);

  const handleOpenInMessenger = () => {
    const messagesPath = currentUserRole === 'client' 
      ? '/client/dashboard/messages'
      : currentUserRole === 'admin'
      ? '/admin/dashboard/messages'
      : '/filmmaker/dashboard/messages';
    
    // Handle broadcast chat specially
    if (chat.id === 'broadcast') {
      navigate(messagesPath);
      closeChat(chat.id);
      closeChatList();
      return;
    }
    
    // Extract recipient ID from room name
    let recipientId = '';
    if (chat.id && chat.id.includes('_')) {
      const parts = chat.id.split('_');
      if (parts.length === 5) {
        // Standard room name format: chat_client_[clientId]_filmmaker_[filmmakerId]
        if (currentUserRole === 'client') {
          recipientId = parts[4]; // filmmaker ID
        } else {
          recipientId = parts[2]; // client ID
        }
      }
    }
    
    // Use URL params like the rest of the app
    const params = new URLSearchParams();
    params.append('name', chat.name);
    params.append('id', recipientId);
    params.append('role', chat.role);
    if (chat.avatar) {
      params.append('avatar', chat.avatar);
    }
    
    navigate(`${messagesPath}?${params.toString()}`);
    closeChat(chat.id);
    // Also close the chat list if it's open
    closeChatList();
  };

  // No minimized view anymore since we removed the minimize button

  // Handle back button on mobile
  const handleMobileBack = () => {
    closeChat(chat.id);
    toggleChatList();
  };

  return (
    <motion.div
      layout
      layoutId={`chat-window-${chat.id}`}
      initial={isMobile ? { y: "100%" } : { scale: 0, opacity: 0 }}
      animate={
        isMobile 
          ? { 
              y: isTransparent ? "calc(100% - 80px)" : 0,
              scale: isTransparent ? 0.9 : 1
            }
          : { 
              scale: 1, 
              opacity: 1
            }
      }
      exit={isMobile ? { y: "100%" } : { scale: 0, opacity: 0 }}
      transition={{
        layout: {
          type: "spring",
          damping: 25,
          stiffness: 200
        },
        type: "spring",
        damping: 30,
        stiffness: 400,
        mass: 0.8
      }}
      className={cn(
        "bg-background border shadow-2xl flex flex-col",
        isMobile
          ? cn(
              "fixed z-50 rounded-t-2xl",
              isTransparent 
                ? "bottom-4 right-4 left-auto h-[60px] w-[200px] rounded-lg"
                : "bottom-0 left-0 right-0 h-[70vh]"
            )
          : "w-96 h-[500px] rounded-lg"
      )}
      style={
        isMobile
          ? { 
              opacity: isTransparent ? 0.7 : 1,
              pointerEvents: isTransparent ? 'none' : 'auto'
            }
          : { 
              bottom: 0,
              right: `${position * 400 + 16}px`,
              position: 'fixed' as const
            }
      }
    >
      {/* Header */}
      {(() => {
        // Check if this is a priority support chat (pro user chatting with admin)
        const isPrioritySupport = currentUserTier === 'pro' && recipientInfo?.id === ADMIN_CLIENT_ID;

        return (
          <div
            className={cn(
              "flex items-center justify-between border-b rounded-t-lg",
              isTransparent && isMobile ? "p-2" : "p-3",
              isPrioritySupport
                ? "bg-gradient-to-r from-yellow-50/50 to-transparent dark:from-yellow-950/20 dark:to-transparent border-yellow-200 dark:border-yellow-800/30"
                : "bg-background/95 border-border"
            )}
            style={{
              pointerEvents: 'auto'
            }}
          >
            <div className="flex items-center gap-2 flex-1">
              {isMobile && !isTransparent && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleMobileBack}
                  className="h-8 w-8"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
              {isBroadcast ? (
                <div className={cn(
                  "rounded-full bg-primary flex items-center justify-center",
                  isTransparent && isMobile ? "h-6 w-6" : "h-8 w-8"
                )}>
                  <Megaphone className={cn(
                    "text-primary-foreground",
                    isTransparent && isMobile ? "h-3 w-3" : "h-4 w-4"
                  )} />
                </div>
              ) : (
                <div className="relative">
                  <Avatar className={cn(
                    isTransparent && isMobile ? "h-6 w-6" : "h-8 w-8",
                    isPrioritySupport && "ring-2 ring-yellow-500/50"
                  )}>
                    <AvatarImage src={chat.avatar} />
                    <AvatarFallback className={cn(
                      isPrioritySupport && "bg-gradient-to-br from-yellow-400 to-yellow-600 text-white"
                    )}>{chat.name[0]}</AvatarFallback>
                  </Avatar>
                  {isPrioritySupport && !isTransparent && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center">
                      <Crown className="h-2.5 w-2.5 text-white" />
                    </div>
                  )}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <div className={cn(
                    "font-semibold truncate",
                    isTransparent && isMobile ? "text-xs" : "text-sm"
                  )}>{chat.name}</div>
                  {isPrioritySupport && !isTransparent && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-gradient-to-br from-yellow-400/30 to-yellow-600/30 text-yellow-700 dark:text-yellow-300 border border-yellow-500/40 text-[9px] font-semibold whitespace-nowrap">
                      <Crown className="h-2 w-2" />
                      Priority
                    </span>
                  )}
                </div>
                {!isTransparent && isBroadcast && (
                  <div className="text-xs text-muted-foreground">Announcements</div>
                )}
              </div>
            </div>
        
        <div className="flex items-center gap-1">
          {/* Mobile Transparency Controls */}
          {isMobile && (
            <div className={cn(
              "flex items-center",
              isTransparent ? "gap-1" : "gap-2 mr-2"
            )}>
              {!isTransparent && (
                <>
                  {!isBroadcast && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setCarouselMedia({ isOpen: true, mediaId: '', messageId: undefined })}
                            className="h-8 w-8"
                          >
                            <Images className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>View Media Gallery</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {/* Hide Open in Messenger button on mobile */}
                </>
              )}
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setIsTransparent(!isTransparent)}
                      className={isTransparent ? "h-6 w-6" : "h-8 w-8"}
                    >
                      {isTransparent ? (
                        <Eye className="h-3 w-3" />
                      ) : (
                        <EyeOff className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {isTransparent ? 'Normal View' : 'See Through Mode'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              {isTransparent && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => closeChat(chat.id)}
                  className="h-6 w-6"
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          )}

          {/* Desktop Controls */}
          {!isMobile && (
            <>
              {!isBroadcast && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setCarouselMedia({ isOpen: true, mediaId: '', messageId: undefined })}
                        className="h-8 w-8"
                      >
                        <Images className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>View Media Gallery</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleOpenInMessenger}
                      className="h-8 w-8"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Open in Messenger</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => closeChat(chat.id)}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
          </div>
        );
      })()}

      {/* Messages Area - Hide when transparent on mobile */}
      {!(isTransparent && isMobile) && (
        <div className="flex-1 overflow-hidden">
          {isBroadcast ? (
            <ScrollArea 
              ref={scrollAreaRef}
              className="h-full p-4"
            >
              <div className="space-y-4">
                {broadcasts && broadcasts.length > 0 ? (
                  broadcasts.map((broadcast) => {
                    const getPriorityIcon = () => {
                      switch (broadcast.priority) {
                        case 'urgent': return <AlertTriangle className="w-4 h-4 text-red-500" />;
                        case 'high': return <AlertCircle className="w-4 h-4 text-orange-500" />;
                        case 'low': return <Info className="w-4 h-4 text-gray-500" />;
                        default: return <Info className="w-4 h-4 text-blue-500" />;
                      }
                    };

                    const getPriorityColor = () => {
                      switch (broadcast.priority) {
                        case 'urgent': return 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800';
                        case 'high': return 'bg-orange-50 border-orange-200 dark:bg-orange-950/20 dark:border-orange-800';
                        case 'low': return 'bg-gray-50 border-gray-200 dark:bg-gray-950/20 dark:border-gray-800';
                        default: return 'bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-800';
                      }
                    };

                    return (
                      <div
                        key={broadcast.id}
                        className={`rounded-lg border p-4 space-y-2 ${getPriorityColor()}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            {getPriorityIcon()}
                            <span className="font-semibold text-sm">{broadcast.title}</span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(broadcast.created_at), 'MMM d, h:mm a')}
                          </span>
                        </div>

                        <div className="pl-6">
                          <p className="text-sm text-foreground/90 whitespace-pre-wrap">
                            {linkifyText(broadcast.content)}
                          </p>
                          
                          {broadcast.metadata?.linked_job_id && broadcast.metadata?.linked_job && (
                            <div className="mt-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                              <div className="flex items-start gap-3">
                                <div className="p-2 bg-primary/10 rounded">
                                  <Briefcase className="w-4 h-4 text-primary" />
                                </div>
                                <div className="flex-1">
                                  <h4 className="font-semibold text-sm mb-1">Job Opportunity</h4>
                                  <p className="text-sm font-medium">
                                    {broadcast.metadata.linked_job.title}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {broadcast.metadata.linked_job.client_project?.client?.company_name || 'Company'}
                                  </p>
                                  <Button 
                                    size="sm" 
                                    className="mt-2"
                                    variant="default"
                                    onClick={() => {
                                      if (currentUserRole === 'filmmaker' || !currentUserRole) {
                                        navigate(`/filmmaker/dashboard/jobs/${broadcast.metadata.linked_job_id}`);
                                      }
                                    }}
                                  >
                                    View & Apply <ArrowRight className="w-3 h-3 ml-1" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {broadcast.expires_at && new Date(broadcast.expires_at) > new Date() && (
                          <div className="pl-6 pt-2 border-t">
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Expires: {format(new Date(broadcast.expires_at), 'MMM d, yyyy')}
                            </p>
                          </div>
                        )}

                        <div className="pl-6 flex items-center gap-2">
                          <Badge variant={
                            broadcast.priority === 'urgent' ? 'destructive' :
                            broadcast.priority === 'high' ? 'default' :
                            'secondary'
                          } className="text-xs">
                            {broadcast.priority.toUpperCase()}
                          </Badge>
                          {broadcast.target_audience !== 'all' && (
                            <Badge variant="outline" className="text-xs">
                              {broadcast.target_audience === 'individual' ? 'Personal' :
                               broadcast.target_audience === 'both' ? 'Multi-Account Users' :
                               broadcast.target_audience === 'clients' ? 'Clients' : 'Filmmakers'}
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                      <Megaphone className="w-8 h-8 text-primary/50" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">No broadcasts</h3>
                    <p className="text-muted-foreground text-sm max-w-sm">
                      You'll see official announcements and updates from Find My Filmmaker here
                    </p>
                  </div>
                )}
              </div>
            </ScrollArea>
          ) : (
            /* Use BidirectionalChat for regular chats - exactly like the main messages page */
            userInfo && recipientInfo ? (
              <BidirectionalChat
                key={`chat-${chat.id}-${currentEntityId}`}
                roomName={chat.id}
                username={currentUserName}
                userInfo={userInfo}
                recipientInfo={recipientInfo}
                hideHeader={true}
                onMediaClick={(mediaId, messageId) => setCarouselMedia({ isOpen: true, mediaId, messageId })}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground">Loading chat...</p>
              </div>
            )
          )}
        </div>
      )}

      {/* Input Area for broadcasts - only show read-only message */}
      {!(isTransparent && isMobile) && isBroadcast && (
        <div className="p-4 border-t bg-muted/50">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Info className="w-4 h-4" />
            <span className="text-sm">Broadcast messages are read-only</span>
          </div>
        </div>
      )}

      {/* Media Carousel Modal */}
      {!isBroadcast && (
        <MediaCarousel
          isOpen={carouselMedia.isOpen}
          onClose={() => setCarouselMedia({ isOpen: false, mediaId: '', messageId: undefined })}
          initialMediaId={carouselMedia.mediaId}
          messageId={carouselMedia.messageId}
          roomName={chat.id}
          onViewAllMedia={() => {
            // Re-open carousel without messageId filter
            setCarouselMedia({ isOpen: true, mediaId: carouselMedia.mediaId, messageId: undefined });
          }}
        />
      )}
    </motion.div>
  );
};